import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { apiGet, apiPostForm } from "../lib/api";

export const Route = createFileRoute("/loan-schedules")({ component: LoanSchedulesPage });

interface LoanRow {
  id: number;
  code: string;
  label: string;
  lender: string;
  accountCode: string;
  bankSource: string;
  schedule: null | { importId: number; filename: string; importedAt: string; rows: number };
}

function LoanSchedulesPage() {
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<Record<number, File | null>>({});
  const [feedback, setFeedback] = useState<string | null>(null);
  const { data, isPending } = useQuery<{ loans: LoanRow[] }>({ queryKey: ["loans"], queryFn: () => apiGet("/api/loans") });
  const upload = useMutation({
    mutationFn: async ({ loanId, file }: { loanId: number; file: File }) => {
      const form = new FormData();
      form.append("file", file);
      return apiPostForm<{ rows: number; duplicate: boolean }>(`/api/loans/${loanId}/schedule`, form);
    },
    onSuccess: (result, variables) => {
      setFeedback(`${result.rows} échéance(s) ${result.duplicate ? "déjà présentes, réactivées" : "importées"}. Elles seront appliquées à la prochaine régénération.`);
      setFiles((current) => ({ ...current, [variables.loanId]: null }));
      queryClient.invalidateQueries({ queryKey: ["loans"] });
    },
  });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Échéanciers comptables</h1>
        <p className="mt-1 text-sm text-gray-500">Import technique servant uniquement à ventiler les prélèvements entre capital, intérêts et frais.</p>
      </div>
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        Le CSV est strict et exprimé en euros. <a href="/api/loans/template.csv" className="font-medium underline">Télécharger le modèle normalisé</a>. Un nouvel import remplace la version active sans supprimer l'historique.
      </div>
      {feedback && <div className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800">{feedback}</div>}
      {upload.isError && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{(upload.error as Error).message}</div>}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500"><tr><th className="px-4 py-2">Emprunt</th><th className="px-4 py-2">Compte</th><th className="px-4 py-2">Source bancaire</th><th className="px-4 py-2">Version active</th><th className="px-4 py-2">Importer</th></tr></thead>
          <tbody>
            {(data?.loans ?? []).map((item) => {
              const file = files[item.id] ?? null;
              const pending = upload.isPending && upload.variables?.loanId === item.id;
              return <tr key={item.id} className="border-t border-gray-100 align-top">
                <td className="px-4 py-3"><div className="font-medium text-gray-900">{item.label}</div><div className="text-xs text-gray-400">{item.code} · {item.lender}</div></td>
                <td className="px-4 py-3 font-mono text-xs">{item.accountCode}</td>
                <td className="px-4 py-3">{item.bankSource === "ca" ? "Crédit Agricole" : "Qonto"}</td>
                <td className="px-4 py-3">{item.schedule ? <><div>{item.schedule.rows} échéance{item.schedule.rows > 1 ? "s" : ""}</div><div className="text-xs text-gray-400">{item.schedule.filename} · {new Date(item.schedule.importedAt).toLocaleDateString("fr-FR")}</div></> : <span className="text-amber-700">Aucun échéancier</span>}</td>
                <td className="px-4 py-3"><div className="flex items-center gap-2"><input type="file" accept=".csv,text/csv" onChange={(event) => setFiles((current) => ({ ...current, [item.id]: event.target.files?.[0] ?? null }))} className="max-w-56 text-xs" /><button disabled={!file || pending} onClick={() => file && upload.mutate({ loanId: item.id, file })} className="rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">{pending ? "Import…" : "Importer"}</button></div></td>
              </tr>;
            })}
            {!isPending && (data?.loans.length ?? 0) === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Aucun emprunt configuré.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400">Après import, relancer la régénération sur la période des prélèvements. Les rapprochements certains seront appliqués ; les autres apparaîtront dans « À décider ».</p>
    </div>
  );
}
